import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { loginUnified, isAuthenticated, userType, user } = useAuth();
  const navigate = useNavigate();

  const [showForceReset, setShowForceReset] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // Note: route gate (ProtectedRoute in App.js) decides admin-vs-staff-portal
  // based on actual RBAC permissions. We just route all staff to /admin and
  // it'll bounce them to /staff-portal if they have no admin permissions.

  useEffect(() => { 
    if (isAuthenticated && !showForceReset) {
      if (userType === 'staff') navigate('/admin');
      else if (userType === 'pt') navigate('/pt/hub');
      else if (user?.is_suspended) navigate('/member/arrears');
      else navigate('/hub');
    }
  }, [isAuthenticated, userType, user, navigate, showForceReset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errors = {};
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Enter a valid email address';
    if (!password) errors.password = 'Password is required';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setLoading(true);
    try { 
      const result = await loginUnified(email, password);
      if (result.user_type === 'staff' && result.must_reset_password) {
        setResetToken(result.access_token);
        setTempPassword(password);
        setShowForceReset(true);
        setLoading(false);
        return;
      }
      if (result.user_type === 'staff') navigate('/admin');
      else if (result.user_type === 'pt') navigate('/pt/hub');
      else if (result.is_suspended) navigate('/member/arrears');
      else navigate('/hub');
    } 
    catch (err) { setError(err.message || 'Login failed'); }
    setLoading(false);
  };

  const handleForceReset = async (e) => {
    e.preventDefault();
    setResetError('');
    if (newPassword.length < 8) { setResetError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setResetError('Passwords do not match'); return; }
    setResetLoading(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${resetToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: tempPassword, new_password: newPassword }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to set password');
      }
      setShowForceReset(false);
      navigate('/admin');
    } catch (err) { setResetError(err.message); }
    setResetLoading(false);
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/hub';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center" data-testid="login-page">
      {showForceReset && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="force-reset-modal">
          <motion.div className="w-full max-w-md bg-uf-surface border border-uf-border rounded-2xl p-8" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-uf-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-uf-red" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              </div>
              <h2 className="text-xl font-bold text-white">Set Your Password</h2>
              <p className="text-uf-textMuted text-sm mt-2">Welcome to UltraFlex. Please create a permanent password.</p>
            </div>
            {resetError && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 mb-4 text-sm rounded-lg">{resetError}</div>}
            <form onSubmit={handleForceReset} className="space-y-4">
              <div>
                <label className="text-xs text-uf-textMuted uppercase tracking-wider mb-1 block">New Password</label>
                <input type="password" placeholder="Min 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-red-500 outline-none" required minLength={8} data-testid="force-reset-new-password" />
              </div>
              <div>
                <label className="text-xs text-uf-textMuted uppercase tracking-wider mb-1 block">Confirm Password</label>
                <input type="password" placeholder="Repeat password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-red-500 outline-none" required minLength={8} data-testid="force-reset-confirm-password" />
              </div>
              <button type="submit" className="btn-primary w-full" disabled={resetLoading} data-testid="force-reset-submit">
                <span>{resetLoading ? 'Setting Password...' : 'Set Password & Continue'}</span>
              </button>
            </form>
          </motion.div>
        </div>
      )}

      <motion.div className="w-full max-w-md mx-4 bg-uf-surface border border-uf-border p-8 md:p-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-8">
          <Logo size="sm" />
          <h1 className="font-heading text-4xl font-bold text-white">Log In</h1>
        </div>
        
        {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 mb-6 text-sm">{error}</div>}
        
        <div className="space-y-3 mb-6">
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition-colors" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-uf-border"></div></div>
          <div className="relative flex justify-center"><span className="px-4 text-sm text-uf-textMuted bg-uf-surface">or continue with email</span></div>
        </div>

        <form onSubmit={handleSubmit} className="uf-form space-y-4" data-testid="login-form">
          <div>
            <input type="email" placeholder="Email Address" value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(f => ({...f, email: ''})); }} data-testid="login-email" className={fieldErrors.email ? 'border-red-500 ring-1 ring-red-500/30' : ''} />
            {fieldErrors.email && <p className="text-red-400 text-xs mt-1.5">{fieldErrors.email}</p>}
          </div>
          <div>
            <input type="password" placeholder="Password" value={password} onChange={e => { setPassword(e.target.value); setFieldErrors(f => ({...f, password: ''})); }} data-testid="login-password" className={fieldErrors.password ? 'border-red-500 ring-1 ring-red-500/30' : ''} />
            {fieldErrors.password && <p className="text-red-400 text-xs mt-1.5">{fieldErrors.password}</p>}
          </div>
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm text-uf-textMuted hover:text-uf-red transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit"><span>{loading ? 'Logging in...' : 'Log In'}</span></button>
        </form>
        <p className="text-center text-uf-textMuted text-sm mt-6">Not a member? <Link to="/membership" className="text-uf-red hover:underline">Join Now</Link></p>
      </motion.div>
    </div>
  );
}
